/**
 * A synthetic "open stadium" impulse response: short pre-delay, a few discrete early slaps off the stands, then a
 * dark exponentially decaying stereo tail. Generated once per AudioContext (no IR file to download).
 */
export interface StadiumIrOptions {
  seconds?: number;
  preDelay?: number;
  /** Deterministic noise source in [0, 1) (tests); defaults to a fixed-seed LCG so every build sounds the same. */
  random?: () => number;
}

const lcg = (seed: number): (() => number) => {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
    return s / 4294967296;
  };
};

export const createStadiumIr = (ctx: BaseAudioContext, opts: StadiumIrOptions = {}): AudioBuffer => {
  const sr = ctx.sampleRate;
  const seconds = opts.seconds ?? 1.6;
  const pre = Math.round((opts.preDelay ?? 0.018) * sr);
  const len = Math.max(pre + 1, Math.round(seconds * sr));
  const rnd = opts.random ?? lcg(0x5eed);
  const ir = ctx.createBuffer(2, len, sr);
  const early = [0.031, 0.047, 0.083, 0.121];
  for (let ch = 0; ch < 2; ch++) {
    const d = ir.getChannelData(ch);
    let lp = 0;
    for (let i = pre; i < len; i++) {
      const t = (i - pre) / sr;
      const env = Math.exp(-t * 4.2);
      // one-pole low-pass on the noise: the tail darkens like air absorption over a big bowl
      lp += (rnd() * 2 - 1 - lp) * (0.55 - 0.35 * Math.min(1, t / seconds));
      d[i] = lp * env * 0.6;
    }
    for (const e of early) {
      const at = pre + Math.round((e + ch * 0.004) * sr);
      if (at < len) d[at] = (d[at] ?? 0) + 0.5 * Math.exp(-e * 9);
    }
  }
  return ir;
};
